import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Location } from '@zerops/models/settings';
import {
  Observable,
  defer,
  of,
  merge,
  forkJoin,
  throwError
} from 'rxjs';
import {
  map,
  catchError,
  timeout,
  startWith,
  scan,
  shareReplay
} from 'rxjs/operators';

export interface LocationWithPing extends Location {
  ping: number | null;
  pingState: 'pending' | 'success' | 'error';
}

@Injectable({ providedIn: 'root' })
export class LocationPingService {
  #http = inject(HttpClient);

  /**
   * Measure ping for a single location
   * Returns ping in milliseconds or null if measurement fails
   */
  measurePing(location: Location): Observable<number> {
    return defer(() => {
      return this.#http.get(location.pingUrl, {
        observe: 'response',
        responseType: 'text'
      }).pipe(
        map(() => {
          const entries = performance.getEntriesByName(location.pingUrl, 'resource');
          const entry = entries[entries.length - 1] as PerformanceResourceTiming | undefined;

          if (!entry) {
            throw new Error('Performance entry not available');
          }

          if (entry.connectEnd && entry.connectStart && entry.connectEnd !== entry.connectStart) {
            // TCP handshake duration ≈ 1 RTT (most accurate latency measurement)
            return Math.round(entry.connectEnd - entry.connectStart);
          }

          if (!entry.responseStart || !entry.requestStart) {
            throw new Error('Performance timing unavailable');
          }

          // Fallback: Total request time (network latency + server processing)
          return Math.round(entry.responseStart - entry.requestStart);
        }),
        timeout(2000), // Reduced from 5000 for faster user experience
        catchError(() => throwError(() => new Error('Ping failed')))
      );
    });
  }

  /**
   * Measure pings for all locations and return location with lowest ping
   * Falls back to first location if all pings fail
   */
  getLocationWithLowestPing(locations: Location[]): Observable<Location> {
    if (!locations || locations.length === 0) {
      return of(null);
    }

    if (locations.length === 1) {
      return of(locations[0]);
    }

    // Measure all pings in parallel
    const pingObservables = locations.map((location) =>
      this.measurePing(location).pipe(
        map((ping) => ({ ...location, ping, pingState: 'success' as const })),
        catchError(() => of({ ...location, ping: null, pingState: 'error' as const }))
      )
    );

    return forkJoin(pingObservables).pipe(
      map((results) => {
        // Filter successful pings
        const successfulPings = results.filter(
          (r): r is typeof r & { ping: number } => r.pingState === 'success' && r.ping !== null
        );

        // If we have successful pings, return location with lowest ping
        if (successfulPings.length > 0) {
          const sorted = successfulPings.sort((a, b) => a.ping - b.ping);
          return sorted[0];
        }

        // Fallback to first location if all pings failed
        return locations[0];
      })
    );
  }

  /**
   * Progressive ping measurement - emits updates as pings complete
   * Useful for UI components that want to show real-time ping updates
   */
  measurePingsProgressive(locations: Location[]): Observable<LocationWithPing[]> {
    if (!locations || locations.length === 0) {
      return of([]);
    }

    return merge(
      ...locations.map((location) =>
        this.measurePing(location).pipe(
          map((ping) => ({ ...location, ping, pingState: 'success' as const })),
          catchError(() => of({ ...location, ping: null, pingState: 'error' as const })),
          startWith({ ...location, ping: null, pingState: 'pending' as const })
        )
      )
    ).pipe(
      scan((acc, result) => {
        const index = acc.findIndex((item) => item.id === result.id);
        if (index !== -1) {
          acc[index] = result;
        } else {
          acc.push(result);
        }
        return [ ...acc ];
      }, [] as LocationWithPing[]),
      map((items) =>
        items.sort((a, b) => {
          // Sort by ping state and ping value
          if (a.pingState === 'pending' && b.pingState !== 'pending') return 1;
          if (b.pingState === 'pending' && a.pingState !== 'pending') return -1;
          if (a.pingState === 'error' && b.pingState !== 'error') return 1;
          if (b.pingState === 'error' && a.pingState !== 'error') return -1;
          if (a.ping === null || b.ping === null) return 0;
          return a.ping - b.ping;
        })
      ),
      shareReplay(1)
    );
  }
}
